import { trackEvent, trackException } from '@/lib/app-insights';
import { getUserManager } from '@/lib/oidc';

export async function getAccessToken(): Promise<string> {
  const um = getUserManager();
  const user = await um.getUser();
  if (user && !user.expired) return user.access_token;
  // No cached user at all (never signed in) is NOT the same case as "signed
  // in but the token expired". Only the latter should force an interactive
  // redirect — falling through to signinSilent()/signinRedirect() for a
  // boot-time anonymous visitor (e.g. usePrincipal() on the public `/`
  // landing route, which calls the non-anonymous /api/auth/me) sends every
  // fresh visitor straight to Ping before the landing/login page ever
  // renders. Mirrors the pre-oidc-client-ts MSAL branch, which threw here
  // instead of calling acquireTokenRedirect.
  if (!user) {
    throw new Error('No active account — login flow must complete before getAccessToken');
  }
  try {
    const renewed = await um.signinSilent();
    if (renewed && !renewed.expired) return renewed.access_token;
    throw new Error('signinSilent returned no valid user');
  } catch {
    try {
      await um.signinRedirect();
    } catch (redirectErr) {
      const err = redirectErr instanceof Error ? redirectErr : new Error(String(redirectErr));
      trackException(err, { source: 'oidc-signin-redirect' });
      throw redirectErr;
    }
    throw new Error('redirecting for interactive auth');
  }
}

export async function getAccessTokenIfSignedIn(): Promise<string | null> {
  const um = getUserManager();
  const user = await um.getUser();
  if (!user) return null;
  if (!user.expired) return user.access_token;
  try {
    const renewed = await um.signinSilent();
    return renewed && !renewed.expired ? renewed.access_token : null;
  } catch (e) {
    const reason = e instanceof Error ? e.name : String(e);
    if (import.meta.env.MODE !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[auth-token] anonymous request proceeding token-less despite cached user', e);
    }
    trackEvent('auth.anonymous_silent_acquire_failed', { reason });
    return null;
  }
}

// Re-export the singleton surface so tests + main.tsx keep one import site.
export {
  getOidcAccount,
  getUserManagerForActions,
  setUserManager,
  _resetForTests,
  type OidcAccountProjection,
} from '@/lib/oidc';
